// Client-side reads of the SAME top-level `/pharmacies/{id}` booking record
// the mobile app (ccs-pharmacy) and public booking site use. Reads are done
// directly via the Firestore client SDK because the rules already declare
// `allow read: if true` on `pharmacies/{id}` and its `rooms`/`availability`
// subcollections — no callable is needed for reads. Writes go through the
// admin callables in lib/callables.ts (bookingAdminApi) since the rules only
// allow the record's ownerUid (the pharmacist) to write directly.
//
// CANONICAL-RECORD RULE — must stay identical to
// functions/src/lib/bookingPharmacyLink.ts (compareCanonical) and
// ccs-pharmacy/src/services/firebase/bookingFirestore.ts. First discard any
// doc already tagged for a DIFFERENT chainId: ccsPharmacyId is only unique
// within a chain, so those belong to another tenant and must never be
// resolved (or written to) from here. Then, among the remaining
// non-superseded docs: oldest `createdAt` wins (missing createdAt = oldest
// of all), ties break on ascending document id.
//
// This file previously preferred a chainId-tagged doc and otherwise took the
// first unordered query result. That disagreed with the mobile app and the
// server helper, so the widget snippet could embed a different record's id
// than the one the pharmacist writes availability to — the patient then saw
// "No availability" against a live schedule. Do not reintroduce a
// chainId-first or first-result heuristic here.
//
// DRIFT WARNING: changing the rule means changing all three files together.

import { getFirebaseClient } from "@workspace/admin-ui";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import type {
  AvailabilityWindow,
  BlockoutDate,
  BookingPharmacy,
  BookingRoom,
  WeeklyAvailabilityOverride,
} from "./bookingTypes";

export interface PharmacistAvailability {
  pharmacistId: string;
  windows: AvailabilityWindow[];
  blockoutDates: BlockoutDate[];
  overrides: WeeklyAvailabilityOverride[];
  updatedAt: string;
}

/**
 * Monday (YYYY-MM-DD) of the ISO week containing `dateStr` (YYYY-MM-DD).
 * Mirrors functions/src/types/booking.ts's getWeekStartDate exactly — must
 * stay in lockstep so "which override applies to this date" agrees with the
 * server.
 */
export function getWeekStartDate(dateStr: string): string {
  const [y, mo, d] = dateStr.split("-").map(Number) as [number, number, number];
  const utcDate = new Date(Date.UTC(y, mo - 1, d));
  const day = utcDate.getUTCDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  utcDate.setUTCDate(utcDate.getUTCDate() + diffToMonday);
  return utcDate.toISOString().split("T")[0]!;
}

export interface BookingResolution {
  pharmacy: BookingPharmacy;
  ambiguous: boolean;
}

// ── Diagnostics ────────────────────────────────────────────────────────────
//
// Chain Admin resolves the SAME canonical-record rule as the widget and the
// server (see the DRIFT WARNING above), but until now nothing logged which
// id/candidates it actually picked. A pharmacy could "look fine" in Chain
// Admin while the widget/server independently resolved a different
// (stale/duplicate) doc for the same ccsPharmacyId — the two surfaces had no
// way to be diffed against each other. Log every resolution so the canonical
// id and full candidate set used to render a schedule can be compared
// directly against the widget's `get_pharmacy_response`/`get_slots_request`
// logs and the server's `ccsGetAvailableSlots` trace for the same pharmacy.
function logResolution(event: string, ctx: Record<string, unknown>): void {
  // eslint-disable-next-line no-console
  console.info(
    `[chain-admin:booking] ${event}`,
    JSON.stringify({ component: "chain-admin-booking", event, ...ctx }),
  );
}

/** Millis for canonical sorting; missing/unrecognized = epoch 0 (oldest). */
function createdAtMillis(value: unknown): number {
  if (
    value &&
    typeof value === "object" &&
    typeof (value as { toMillis?: unknown }).toMillis === "function"
  ) {
    return (value as { toMillis: () => number }).toMillis();
  }
  return 0;
}

/** The shared canonical comparator — see the DRIFT WARNING at the top. */
function compareCanonical(
  a: { id: string; data: Record<string, unknown> },
  b: { id: string; data: Record<string, unknown> },
): number {
  const byAge = createdAtMillis(a.data.createdAt) - createdAtMillis(b.data.createdAt);
  if (byAge !== 0) return byAge;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

async function loadCandidates(
  chainId: string,
  ccsPharmacyId: string,
): Promise<Array<{ id: string; data: Record<string, unknown> }>> {
  const { db } = getFirebaseClient();
  // No limit and no orderBy: an orderBy would drop legacy docs that have no
  // createdAt, and a limit on an unordered query could hide the canonical one.
  const snap = await getDocs(
    query(collection(db, "pharmacies"), where("ccsPharmacyId", "==", ccsPharmacyId)),
  );
  return snap.docs
    .map((d) => ({ id: d.id, data: d.data() as Record<string, unknown> }))
    .filter((d) => !d.data.supersededBy)
    // Tenant boundary — never resolve another chain's record. Untagged
    // legacy docs stay eligible (the server backfills their chainId).
    .filter((d) => !d.data.chainId || d.data.chainId === chainId)
    .sort(compareCanonical);
}

/**
 * Resolves the single canonical booking-pharmacy record for a CCS pharmacy.
 * `ambiguous` means duplicates still exist and need reconciling — the
 * returned record is still the one every other surface resolves to, so it is
 * safe to use, but an admin should be warned.
 */
export async function findBookingPharmacy(
  chainId: string,
  ccsPharmacyId: string,
): Promise<BookingResolution | null> {
  const docs = await loadCandidates(chainId, ccsPharmacyId);
  if (docs.length === 0) {
    logResolution("resolve_none", { chainId, ccsPharmacyId });
    return null;
  }
  const chosen = docs[0]!;
  logResolution("resolve", {
    chainId,
    ccsPharmacyId,
    resolvedId: chosen.id,
    candidateIds: docs.map((d) => d.id),
    ambiguous: docs.length > 1,
  });
  return {
    pharmacy: toBookingPharmacy(chosen.id, chosen.data),
    ambiguous: docs.length > 1,
  };
}

/**
 * Returns every live booking-pharmacy record for a CCS pharmacy, canonical
 * first. These are duplicates of ONE pharmacy, not distinct locations — only
 * the canonical record's id may be handed out in a booking link or embed
 * snippet. Callers use the tail purely to warn about unreconciled data.
 */
export async function findAllBookingPharmacies(
  chainId: string,
  ccsPharmacyId: string,
): Promise<BookingPharmacy[]> {
  const docs = await loadCandidates(chainId, ccsPharmacyId);
  logResolution("resolve_all", {
    chainId,
    ccsPharmacyId,
    candidateIds: docs.map((d) => d.id),
    canonicalId: docs[0]?.id ?? null,
  });
  return docs.map((d) => toBookingPharmacy(d.id, d.data));
}

function toBookingPharmacy(id: string, data: Record<string, unknown>): BookingPharmacy {
  return {
    id,
    name: String(data.name ?? ""),
    address: String(data.address ?? ""),
    eircode: String(data.eircode ?? ""),
    slug: String(data.slug ?? ""),
    primaryColor: typeof data.primaryColor === "string" ? data.primaryColor : undefined,
    logoUrl: typeof data.logoUrl === "string" ? data.logoUrl : undefined,
    ownerUid: String(data.ownerUid ?? ""),
    ccsPharmacyId: typeof data.ccsPharmacyId === "string" ? data.ccsPharmacyId : undefined,
    chainId: typeof data.chainId === "string" ? data.chainId : undefined,
    createdAt: String(data.createdAt ?? ""),
    updatedAt: String(data.updatedAt ?? ""),
  };
}

export async function getBookingPharmacyById(id: string): Promise<BookingPharmacy | null> {
  const { db } = getFirebaseClient();
  const snap = await getDoc(doc(db, "pharmacies", id));
  if (!snap.exists()) return null;
  return toBookingPharmacy(snap.id, snap.data() as Record<string, unknown>);
}

export async function getBookingRooms(bookingPharmacyId: string): Promise<BookingRoom[]> {
  const { db } = getFirebaseClient();
  const snap = await getDocs(
    collection(db, "pharmacies", bookingPharmacyId, "rooms"),
  );
  return snap.docs.map((d) => {
    const data = d.data() as Record<string, unknown>;
    return {
      id: d.id,
      pharmacyId: bookingPharmacyId,
      name: String(data.name ?? ""),
      description: typeof data.description === "string" ? data.description : undefined,
      isActive: Boolean(data.isActive ?? true),
      createdAt: String(data.createdAt ?? ""),
    };
  });
}

export async function getPharmacistAvailabilities(
  bookingPharmacyId: string,
): Promise<PharmacistAvailability[]> {
  const { db } = getFirebaseClient();
  const snap = await getDocs(
    collection(db, "pharmacies", bookingPharmacyId, "availability"),
  );
  const results = snap.docs.map((d) => {
    const data = d.data() as Record<string, unknown>;
    return {
      pharmacistId: d.id,
      windows: Array.isArray(data.windows) ? (data.windows as PharmacistAvailability["windows"]) : [],
      blockoutDates: Array.isArray(data.blockoutDates)
        ? (data.blockoutDates as PharmacistAvailability["blockoutDates"])
        : [],
      // Previously not read at all here — see the WeeklyAvailabilityOverride
      // doc comment in bookingTypes.ts for why that made override-caused
      // empty schedules invisible in Chain Admin.
      overrides: Array.isArray(data.overrides)
        ? (data.overrides as PharmacistAvailability["overrides"])
        : [],
      updatedAt: String(data.updatedAt ?? ""),
    };
  });
  logResolution("availability_docs_read", {
    bookingPharmacyId,
    pharmacistIds: results.map((r) => r.pharmacistId),
    windowCounts: Object.fromEntries(results.map((r) => [r.pharmacistId, r.windows.length])),
    overrideWeeks: Object.fromEntries(
      results.map((r) => [r.pharmacistId, r.overrides.map((o) => o.weekStartDate)]),
    ),
  });
  return results;
}
